import { FormikValues, setIn } from 'formik';
import * as Yup from 'yup';

import { AddStudentsStepForm, StudentFormEntry } from '../../../types';
import { SourceKind, StudentLoginType } from 'types/schema-generated-types';

export const secretCodeTest = Yup.mixed()
  .test('Empty String', 'Field is required', function(
    this,
    fieldValue: { loginType: StudentLoginType; code: string }
  ): boolean {
    if (fieldValue) {
      return Boolean(fieldValue.code);
    }
    return false;
  })
  .test('Minimum Length', 'Too short', function(
    this,
    fieldValue: { loginType: StudentLoginType; code: string }
  ) {
    if (fieldValue) {
      return fieldValue.code.length >= 2 || fieldValue.code === '';
    }
    return false;
  })
  .test('Alphanumeric', 'Invalid characters', function(
    this,
    fieldValue: { loginType: StudentLoginType; code: string }
  ) {
    if (fieldValue) {
      if (fieldValue.code === '') {
        return true;
      }
      return /^[A-Z0-9]+$/i.test(fieldValue.code);
    }
    return false;
  })
  .required('Field is required.');

export const usernameTest = Yup.string()
  .min(4, 'Too short.')
  .max(24, 'Too long.')
  .test('Alphanumeric', 'Invalid characters.', function(this, fieldValue) {
    return /^[\w\-#@.\s]+$/.test(fieldValue?.trim());
  })
  .required('Field is required');
const getStudentValidation = (type?: SourceKind, usernames?: Array<string>) => {
  return (values: FormikValues): object => {
    // Error object returned if there are errors
    const errors: AddStudentsStepForm = { students: [] };
    // Error object used to track number of actual errors
    const totalErrors: AddStudentsStepForm = { students: [] };
    function flatten(): { [key: string]: number } {
      const arr: Array<StudentFormEntry> = values.students;
      const obj: { [key: string]: number } = {};
      for (let i = 0; i < arr.length; i += 1) {
        if (arr[i].username) {
          const key = arr[i].username;
          if (key) {
            if (typeof obj[key] === 'number') {
              obj[key] = obj[key] + 1;
            } else {
              obj[key] = 0;
            }
          }
        }
      }
      return obj;
    }
    const duplicateErrors = flatten();
    const defaultStudentObject: any = {
      firstName: undefined,
      lastName: undefined,
      username: undefined,
      secretCode: undefined,
      email: undefined,
      gradeLevel: undefined,
      group: undefined
    };

    const schema = {
      firstName: Yup.string().required('Field is required'),
      lastName: Yup.string().required('Field is required'),
      username: usernameTest.test('Duplicate', 'Duplicate username', function(this, fieldValue) {
        const found =
          usernames &&
          usernames.find(
            (username: string) => username.trim().toLowerCase() === fieldValue.trim().toLowerCase()
          );
        return !found;
      }),
      secretCode: secretCodeTest,

      gradeLevel: Yup.string().required('Field is required'),
      group: Yup.object()
    };
    const emailSchema =
      type === SourceKind.GOOGLE
        ? {
            email: Yup.string()
              .email('Must be an valid email')
              .required('Field is required')
          }
        : { email: Yup.string().email('Must be an valid email') };
    const studentSchema = Yup.object().shape({ ...schema, ...emailSchema });

    values?.students?.forEach((student: StudentFormEntry) => {
      // TODO: all of these form types should be re-worked at some point

      const values = Object.values(student);

      // Checks for number of filled out fields
      const undefinedCount = values.filter((x: any) => {
        if (typeof x === 'object') {
          return x.code === '' || x.code === undefined;
        }
        return x === undefined || x === '';
      }).length;

      // If no fields have been filled out it will push empty student object to
      // errors object to keep index count correct.
      // Notice that it doesn't push error object to total errors to keep acutal
      // count of errors correct.
      if (undefinedCount === Object.keys(defaultStudentObject).length) {
        errors.students.push(student);
        return;
      }
      const duplicateUsernameError = student.username ? duplicateErrors[student.username] : false;
      try {
        // Validates student object against schema.

        studentSchema.validateSync(student, {
          stripUnknown: false,
          strict: true,
          abortEarly: false
        });

        if (duplicateUsernameError) {
          throw new Error('Duplicate usernames');
        }
        // If error wasn't thrown, then student object is valid.
        errors.students.push(defaultStudentObject);
      } catch (validationError) {
        let studentErrors = duplicateUsernameError
          ? { ...defaultStudentObject, username: 'Duplicate username' }
          : { ...defaultStudentObject };

        validationError?.inner?.forEach((error: { path: string; message: string }) => {
          studentErrors = {
            ...studentErrors,
            ...setIn(studentErrors, error.path, error.message)
          };
        });

        // Pushes object to total errors and errors object.
        totalErrors.students.push(studentErrors);
        errors.students.push(studentErrors);
      }
    });

    // If there are in total errors, it will return the shaped errors object,
    // otherwise it will return empty errors object
    return totalErrors.students.length ? errors : {};
  };
};

export default getStudentValidation;
